import type { GetMessagesQueryVariables } from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useQuery, useSubscription } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { INBOX_MESSAGES_UPDATED } from '#src/graphql/subscriptions/inbox-messages-updated';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { useUser } from '#src/providers/user-provider';
import { resolveFocusParamToRootId } from '#src/utils/message-thread-focus';

export interface UseMessagesQueryPanelOptions {
  variables: GetMessagesQueryVariables;
  /** When true, skips the messages query and inbox subscription refetch. */
  skip: boolean;
}

/**
 * Shared inbox message list: query, realtime refetch, sort, URL thread focus.
 */
export function useMessagesQueryPanel({
  variables,
  skip,
}: UseMessagesQueryPanelOptions) {
  const { user } = useUser();
  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const {
    data: messagesData,
    error: rawMessagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip,
  });

  useSubscription(INBOX_MESSAGES_UPDATED, {
    skip: skip || !user?.email?.trim(),
    onData: () => {
      void refetchMessages();
    },
  });

  const messagesError = skip ? undefined : rawMessagesError;

  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (skip || isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    skip,
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  return {
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    expandThread,
    collapseThread,
    messagesQueryActive: !skip,
  };
}
